import { jsPDF } from "jspdf";

const METHOD_LABEL = {
  transferencia: "Transferencia",
  tarjeta_debito: "Tarjeta de débito",
  mercado_pago: "Mercado Pago",
  efectivo: "Efectivo",
};

const MES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function fmtARS(n) {
  return `$${(Number(n) || 0).toLocaleString("es-AR")}`;
}

function formatMonth(ym) {
  if (!ym || ym.length < 7) return ym || "";
  const [y, m] = ym.split("-");
  const idx = parseInt(m, 10) - 1;
  if (idx < 0 || idx > 11) return ym;
  return `${MES[idx]} ${y}`;
}

/**
 * Genera un PDF con el resumen histórico de pagos de un atleta:
 * - Encabezado Athlon
 * - Datos del atleta + período
 * - Totales y desglose por método
 * - Detalle mes por mes con comprobantes
 *
 * @param {object} summary - shape devuelto por GET /users/{id}/payments/summary
 * @param {object} options - { yearFilter: "all" | "YYYY" }
 */
export async function generatePaymentSummaryPDF(summary, options = {}) {
  const { yearFilter = "all" } = options;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 40; // margen
  let y = M;

  const months =
    yearFilter === "all"
      ? summary.by_month || []
      : (summary.by_month || []).filter((m) => m.month.startsWith(yearFilter));

  // ----- Header -----
  doc.setFillColor(10, 10, 10);
  doc.rect(0, 0, pageW, 90, "F");
  doc.setTextColor(255, 59, 48);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("ATHLON SPORTS", M, 40);
  doc.setTextColor(220, 220, 220);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text("Training Control · Resumen de Pagos", M, 58);
  doc.setFontSize(8);
  doc.setTextColor(170, 170, 170);
  doc.text(
    `Generado: ${new Date().toLocaleString("es-AR")}`,
    pageW - M, 58, { align: "right" }
  );

  y = 110;

  // ----- Datos del atleta -----
  doc.setTextColor(20, 20, 20);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("ATLETA", M, y);
  y += 18;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Nombre: ${summary.user?.name || "—"}`, M, y); y += 14;
  doc.text(`Email: ${summary.user?.email || "—"}`, M, y); y += 14;
  if (summary.user?.monthly_fee) {
    doc.text(`Cuota mensual configurada: ${fmtARS(summary.user.monthly_fee)}`, M, y);
    y += 14;
  }
  doc.text(`Período: ${yearFilter === "all" ? "Histórico completo" : `Año ${yearFilter}`}`, M, y);
  y += 22;

  // ----- Totales -----
  const t = summary.totals || {};
  const totalPaid = (months || []).reduce((acc, mo) => acc + (mo.total || 0), 0);
  const totalCount = (months || []).reduce((acc, mo) => acc + (mo.count || 0), 0);

  doc.setFillColor(245, 245, 245);
  doc.rect(M, y, pageW - 2 * M, 70, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.text("TOTAL PAGADO", M + 12, y + 18);
  doc.text("COMPROBANTES", M + 180, y + 18);
  doc.text("PRIMER PAGO", M + 320, y + 18);
  doc.text("ÚLTIMO PAGO", M + 440, y + 18);

  doc.setTextColor(255, 59, 48);
  doc.setFontSize(15);
  doc.text(fmtARS(totalPaid), M + 12, y + 42);
  doc.setTextColor(20, 20, 20);
  doc.text(String(totalCount), M + 180, y + 42);
  doc.setFontSize(11);
  doc.text(t.first_payment_date || "—", M + 320, y + 42);
  doc.text(t.last_payment_date || "—", M + 440, y + 42);
  y += 90;

  // ----- Por método -----
  const byMethod = Object.entries(summary.by_method || {});
  if (byMethod.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(20, 20, 20);
    doc.text("Total por método", M, y); y += 16;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    byMethod.forEach(([m, data]) => {
      const label = METHOD_LABEL[m] || m;
      doc.text(`• ${label}: ${fmtARS(data.total)} (${data.count} pagos)`, M + 10, y);
      y += 14;
    });
    y += 10;
  }

  // ----- Detalle por mes -----
  if (months.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.text("No hay pagos registrados en el período seleccionado.", M, y);
  } else {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(20, 20, 20);
    doc.text("Detalle por mes", M, y); y += 18;

    const ensureSpace = (h) => {
      if (y + h > pageH - 40) {
        doc.addPage();
        y = M;
      }
    };

    months.forEach((mo) => {
      ensureSpace(50);
      // Encabezado de mes
      doc.setFillColor(10, 10, 10);
      doc.rect(M, y - 12, pageW - 2 * M, 22, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text(formatMonth(mo.month).toUpperCase(), M + 8, y + 3);
      doc.setTextColor(255, 59, 48);
      doc.text(fmtARS(mo.total), pageW - M - 8, y + 3, { align: "right" });
      y += 22;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(40, 40, 40);

      // Tabla simple
      (mo.payments || []).forEach((p) => {
        ensureSpace(16);
        const left = `${p.receipt_number || "—"}  ·  ${p.payment_date || ""}  ·  ${METHOD_LABEL[p.method] || p.method}`;
        const right = fmtARS(p.amount);
        doc.text(left, M + 8, y + 10);
        doc.text(right, pageW - M - 8, y + 10, { align: "right" });
        if (p.concept) {
          doc.setTextColor(110, 110, 110);
          doc.setFontSize(8);
          doc.text(p.concept, M + 8, y + 20);
          doc.setTextColor(40, 40, 40);
          doc.setFontSize(9);
          y += 22;
        } else {
          y += 14;
        }
      });
      // Separador
      doc.setDrawColor(220, 220, 220);
      doc.line(M, y + 3, pageW - M, y + 3);
      y += 12;
    });
  }

  // ----- Footer en cada página -----
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(
      `Athlon Sports · Training Control · Resumen generado automáticamente`,
      pageW / 2, pageH - 18, { align: "center" }
    );
    doc.text(`Página ${i} de ${totalPages}`, pageW - M, pageH - 18, { align: "right" });
  }

  const safeName = (summary.user?.name || "atleta").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
  const periodo = yearFilter === "all" ? "historico" : yearFilter;
  doc.save(`resumen_pagos_${safeName}_${periodo}.pdf`);
}

export default generatePaymentSummaryPDF;
